import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  TextField,
  Typography,
  Grid,
  Divider,
  Checkbox,
  Stack,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Chip,
  InputAdornment,
  CircularProgress,
  useMediaQuery,
  FormControlLabel,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { hostApiClient } from '../../hostApiClient';
import { CreateSpaceRequest, SpaceScheduleWeeklyItem, SpaceWeeklySchedule } from '../../models/Space';
import MediaUploader from '../../components/MediaUploader';
import { useTranslation } from 'react-i18next';
import { Autocomplete as GooglePlacesAutocomplete } from '@react-google-maps/api';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { OpenInNew } from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { SpaceAmenity } from '../../models/SpaceAmenity';
import { SpaceGear } from '../../models/SpaceGear';
import { getAmenityLabel, amenityIcons } from '../../amenities';
import { getGearLabel, gearIcons } from '../../gears';
import AmountInput from '../../components/AmountInput';
import { useGoogleMaps } from '../../contexts/GoogleMapsContext';
import { countryInfo } from '../../countries';

const defaultWeeklySchedule: SpaceWeeklySchedule = {
  monday: { periodStart: '09:00', periodEnd: '18:00', openAllDay: false, dayOff: false },
  tuesday: { periodStart: '09:00', periodEnd: '18:00', openAllDay: false, dayOff: false },
  wednesday: { periodStart: '09:00', periodEnd: '18:00', openAllDay: false, dayOff: false },
  thursday: { periodStart: '09:00', periodEnd: '18:00', openAllDay: false, dayOff: false },
  friday: { periodStart: '09:00', periodEnd: '18:00', openAllDay: false, dayOff: false },
  saturday: { periodStart: '10:00', periodEnd: '16:00', openAllDay: false, dayOff: false },
  sunday: { periodStart: '', periodEnd: '', openAllDay: false, dayOff: true },
};

const currencySymbols: Record<string, string> = {
  USD: '$',
  EUR: '€',
  PLN: 'zł',
  GBP: '£',
  AUD: 'A$',
};

const CreateSpacePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isLoaded, loadError } = useGoogleMaps();

  const [editValues, setEditValues] = useState<CreateSpaceRequest>({
    name: '',
    description: '',
    googleMapsUrl: '',
    googlePlaceId: '',
    imageUrls: [],
    howToFindVideoUrls: [],
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    countryCode: '',
    weeklySchedule: defaultWeeklySchedule,
    amenities: [],
    gears: [],
    currencyCode: 'PLN',
    latitude: 0,
    longitude: 0,
  });

  const [gears, setGears] = useState<SpaceGear[]>([]);
  const [newGear, setNewGear] = useState<SpaceGear>({ gearType: '', details: '', pricePerH: 0 });
  const [amenities, setAmenities] = useState<SpaceAmenity[] | []>([]);
  const [newAmenity, setNewAmenity] = useState<SpaceAmenity>({ amenityType: '', details: '', pricePerH: 0 });
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState<google.maps.places.PlaceResult | null>(null);
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>({
    lat: 52.2297, // Warsaw's latitude
    lng: 21.0122, // Warsaw's longitude
  });
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleAddAmenity = () => {
    if (!newAmenity.amenityType.trim()) return;
  
    const updatedAmenities = [...amenities, { ...newAmenity, details: newAmenity.details?.trim() || '' }];
    setAmenities(updatedAmenities);
    setEditValues((prev) => prev ? { ...prev, amenities: updatedAmenities } : prev);
    setNewAmenity({ amenityType: '', details: '', pricePerH: 0 });
    setHasChanges(true);
  };

  const handleAddGear = () => {
    if (!newGear.gearType.trim()) return;
  
    const updatedGears = [...gears, { ...newGear, details: newGear.details?.trim() || '' }];
    setGears(updatedGears);
    setEditValues((prev) => prev ? { ...prev, gears: updatedGears } : prev);
    setNewGear({ gearType: '', details: '', pricePerH: 0 });
    setHasChanges(true);
  };

  const handleDeleteGear = (index: number) => {
    const updatedGears = gears.filter((_, i) => i !== index);
    setGears(updatedGears);
    setEditValues((prev) => prev ? { ...prev, gears: updatedGears } : prev);
    setHasChanges(true);
  };
  
  const handleDeleteAmenity = (index: number) => {
    const updatedAmenities = amenities.filter((_, i) => i !== index);
    setAmenities(updatedAmenities);
    setEditValues((prev) => prev ? { ...prev, amenities: updatedAmenities } : prev);
    setHasChanges(true);
  };

  const handleChange = (field: keyof CreateSpaceRequest, value: any) => {
    setEditValues({ ...editValues, [field]: value });
    setHasChanges(true);
  };

  const handleScheduleChange = (
    day: keyof SpaceWeeklySchedule,
    field: keyof SpaceScheduleWeeklyItem,
    value: any
  ) => {
    const newSchedule = { ...editValues.weeklySchedule };
    const updatedDay = {
      ...newSchedule[day],
      [field]: typeof value === 'boolean' ? value : value.target?.value || value,
    };

    if (field === 'openAllDay' && value === true) {
      updatedDay.dayOff = false;
      updatedDay.periodStart = '';
      updatedDay.periodEnd = '';
    }
    
    if (field === 'dayOff' && value === true) {
      updatedDay.openAllDay = false;
      updatedDay.periodStart = '';
      updatedDay.periodEnd = '';
    }
    

    newSchedule[day] = updatedDay;
    setEditValues({ ...editValues, weeklySchedule: newSchedule });
    setHasChanges(true);
  };

  const renderTimeCell = (field: 'periodStart' | 'periodEnd') => (params: GridRenderCellParams) => {
    const { openAllDay, dayOff } = params.row;
    const value = params.row[field];
    const timeValue = value ? dayjs(`1970-01-01T${value}`) : null;

    const handleTimeChange = (newValue: Dayjs | null) => {
      if (!newValue) return;
      const formatted = newValue.format('HH:mm');
      handleScheduleChange(params.row.dayKey, field, formatted);
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TimePicker
          value={timeValue}
          onChange={handleTimeChange}
          minutesStep={30}
          disabled={openAllDay || dayOff}
          ampm={false}
          slotProps={{
            textField: {
              fullWidth: true,
              size: 'small',
              sx: { mt: 1 },
            },
          }}
        />
      </LocalizationProvider>
    );
  };

  const scheduleColumns: GridColDef[] = [
    { field: 'dayTranslated', headerName: t('space.day', 'Day'), flex: 1 },
    {
      field: 'periodStart',
      headerName: t('space.open', 'Open'),
      flex: 1,
      renderCell: renderTimeCell('periodStart'),
    },
    {
      field: 'periodEnd',
      headerName: t('space.close', 'Close'),
      flex: 1,
      renderCell: renderTimeCell('periodEnd'),
    },
    {
      field: 'openAllDay',
      headerName: t('space.24hOpen', '24h Open'),
      flex: 1,
      renderCell: (params) => (
        <Checkbox
          checked={params.value}
          onChange={(e) => handleScheduleChange(params.row.dayKey, 'openAllDay', e.target.checked)}
        />
      ),
    },
    {
      field: 'dayOff',
      headerName: t('space.dayOff', 'Day Off'),
      flex: 1,
      renderCell: (params) => (
        <Checkbox
          checked={params.value}
          onChange={(e) => handleScheduleChange(params.row.dayKey, 'dayOff', e.target.checked)}
        />
      ),
    },
  ];

  const handleSave = async () => {
    setHasSubmitted(true);
  
    const isInvalid =
      !editValues.name.trim() ||
      !editValues.description.trim() ||
      !editValues.googleMapsUrl.trim() ||
      !editValues.googleMapsUrl.startsWith('http') ||
      !editValues.city.trim();
  
    if (isInvalid) {
      return; // Do not proceed if required fields are invalid
    }
  
    try {
      await hostApiClient.createSpace(editValues);
      navigate('/host/spaces');
    } catch (err) {
      console.error(t('space.createError', 'Failed to create space:'), err);
    }
  };
  

  const handleCancel = () => {
    navigate('/host/spaces');
  };

  const handlePlaceSelect = useCallback((place: google.maps.places.PlaceResult) => {
    setSelectedPlace(place);
    
    if (place.geometry?.location) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setMapCenter({ lat, lng });
      
      // Get address components
      const addressComponents = place.address_components || [];
      let countryCode = '';
      let city = '';
      let state = '';
      let zipCode = '';
      let addressLine1 = '';
      let addressLine2 = '';
      
      // Extract address components
      addressComponents.forEach((component) => {
        const types = component.types;
        if (types.includes('country')) {
          countryCode = component.short_name;
        } else if (types.includes('locality')) {
          city = component.long_name;
        } else if (types.includes('administrative_area_level_1')) {
          state = component.long_name;
        } else if (types.includes('postal_code')) {
          zipCode = component.long_name;
        } else if (types.includes('route')) {
          addressLine1 = component.long_name;
        } else if (types.includes('sublocality')) {
          addressLine2 = component.long_name;
        }
      });

      // Get country info for currency
      const country = countryInfo[countryCode] || { currency: 'USD' };

      setEditValues((prev) => ({
        ...prev,
        googleMapsUrl: place.url || '',
        googlePlaceId: place.place_id || '',
        latitude: lat,
        longitude: lng,
        city,
        state,
        zipCode,
        addressLine1,
        addressLine2,
        countryCode,
        currencyCode: country.currency,
      }));
      setHasChanges(true);
    }
  }, []);

  const onLoad = useCallback((autocomplete: google.maps.places.Autocomplete) => {
    autocompleteRef.current = autocomplete;
    autocomplete.setFields(['address_components', 'geometry', 'formatted_address', 'url', 'place_id', 'name']);
  }, []);

  const onPlaceChanged = useCallback(() => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      handlePlaceSelect(place);
    }
  }, [handlePlaceSelect]);

  if (loadError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography variant="h6" color="error">{t('common.errorLoadingMaps')}</Typography>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: { xs: 2, md: 4 }, pb: { xs: 8, md: 10 }, maxWidth: 1000, width: '100%', mx: 'auto'}}>
        <Card elevation={3} sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('space.createNew', 'Create New Space')}
          </Typography>
          <Divider sx={{ mb: { xs: 2, md: 3 } }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={t('space.name', 'Space Name')}
                  value={editValues.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  fullWidth
                  required
                  error={hasSubmitted && !editValues.name.trim()}
                  helperText={hasSubmitted && !editValues.name.trim() ? t('space.nameRequired', 'Space name is required') : ' '}                
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label={t('space.description', 'Description')}
                  value={editValues.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                  fullWidth
                  multiline
                  rows={10}
                  required
                  error={hasSubmitted && !editValues.description.trim()}
                  helperText={hasSubmitted && !editValues.description.trim() ? t('space.descriptionRequired', 'Description is required') : ' '}
                />
              </Grid>
              <Grid item xs={12}>
                <GooglePlacesAutocomplete
                  onLoad={onLoad}
                  onPlaceChanged={onPlaceChanged}
                  restrictions={{ country: 'pl' }}
                >
                  <TextField
                    inputRef={inputRef}
                    label={t('space.placeLocation')}
                    fullWidth
                    placeholder={t('space.placeLocationPlaceholder', 'Search for a location in Poland')}
                    required
                    error={hasSubmitted && !selectedPlace}
                    helperText={hasSubmitted && !selectedPlace ? t('space.placeLocationRequired', 'Please select a location from Google Maps') : ' '}
                  />
                </GooglePlacesAutocomplete>

                <Box sx={{ mt: 2, height: { xs: 200, sm: 300 }, width: '100%' }}>
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={mapCenter}
                    zoom={15}
                  >
                    {selectedPlace?.geometry?.location && (
                      <Marker
                        position={{
                          lat: selectedPlace.geometry.location.lat(),
                          lng: selectedPlace.geometry.location.lng(),
                        }}
                      />
                    )}
                  </GoogleMap>
                </Box>
              </Grid>

              {selectedPlace && (
                <>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('space.city')}
                      value={editValues.city || ''}
                      fullWidth
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('space.state')}
                      value={editValues.state || ''}
                      fullWidth
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('space.zipCode')}
                      value={editValues.zipCode || ''}
                      fullWidth
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('space.addressLine1')}
                      value={editValues.addressLine1 || ''}
                      onChange={(e) => handleChange('addressLine1', e.target.value)}
                      fullWidth
                      required
                      error={hasSubmitted && !editValues.addressLine1.trim()}
                      helperText={hasSubmitted && !editValues.addressLine1.trim() ? t('space.addressLine1Required', 'Address Line 1 is required') : ' '}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('space.addressLine2')}
                      value={editValues.addressLine2 || ''}
                      onChange={(e) => handleChange('addressLine2', e.target.value)}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t('space.googleMapsURL')}
                      value={editValues.googleMapsUrl || ''}
                      fullWidth
                      required
                      type="url"
                      onChange={(e) => handleChange('googleMapsUrl', e.target.value)}
                      InputProps={{
                        endAdornment: editValues.googleMapsUrl && (
                          <InputAdornment position="end">
                            <IconButton
                              edge="end"
                              onClick={() => window.open(editValues.googleMapsUrl, '_blank')}
                              size="small"
                            >
                              <OpenInNew />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <MediaUploader
                  label={t('space.spaceImages', 'Space Images')}
                  initialMedia={editValues.imageUrls || []}
                  onMediaChange={(newMedia) => {
                    handleChange('imageUrls', newMedia);
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <MediaUploader
                  label={t('space.howToFindVideo', 'How to Find the Space (Video/Image)')}
                  initialMedia={editValues.howToFindVideoUrls || []}
                  onMediaChange={(newMedia) => {
                    handleChange('howToFindVideoUrls', newMedia);
                  }}
                  maxFiles={1}
                />
              </Grid>

              {editValues.weeklySchedule && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom mt={2}>
                    {t('space.weeklySchedule', 'Weekly Schedule')}
                  </Typography>

                  {isMobile ? (
                    <Stack spacing={2} mt={2} mb={2}>
                      {Object.entries(editValues.weeklySchedule).map(([day, schedule]) => {
                        const dayLabel = t(`space.days.${String(day).toLowerCase()}`, String(day));
                        const openAllDay = (schedule as any).openAllDay;
                        const dayOff = (schedule as any).dayOff;
                        const periodStart = (schedule as any).periodStart;
                        const periodEnd = (schedule as any).periodEnd;
                        const timeStart = periodStart ? dayjs(`1970-01-01T${periodStart}`) : null;
                        const timeEnd = periodEnd ? dayjs(`1970-01-01T${periodEnd}`) : null;

                        return (
                          <Box key={day} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              {dayLabel}
                            </Typography>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <Grid container spacing={1}>
                                <Grid item xs={12} sm={6}>
                                  <TimePicker
                                    label={t('space.open', 'Open')}
                                    value={timeStart}
                                    onChange={(newValue) => {
                                      if (!newValue) return;
                                      const formatted = newValue.format('HH:mm');
                                      handleScheduleChange(day as keyof SpaceWeeklySchedule, 'periodStart', formatted);
                                    }}
                                    minutesStep={30}
                                    disabled={openAllDay || dayOff}
                                    ampm={false}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TimePicker
                                    label={t('space.close', 'Close')}
                                    value={timeEnd}
                                    onChange={(newValue) => {
                                      if (!newValue) return;
                                      const formatted = newValue.format('HH:mm');
                                      handleScheduleChange(day as keyof SpaceWeeklySchedule, 'periodEnd', formatted);
                                    }}
                                    minutesStep={30}
                                    disabled={openAllDay || dayOff}
                                    ampm={false}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={openAllDay}
                                        onChange={(e) => handleScheduleChange(day as keyof SpaceWeeklySchedule, 'openAllDay', e.target.checked)}
                                      />
                                    }
                                    label={t('space.24hOpen', '24h Open')}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={dayOff}
                                        onChange={(e) => handleScheduleChange(day as keyof SpaceWeeklySchedule, 'dayOff', e.target.checked)}
                                      />
                                    }
                                    label={t('space.dayOff', 'Day Off')}
                                  />
                                </Grid>
                              </Grid>
                            </LocalizationProvider>
                          </Box>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Grid item xs={12} mt={2} mb={2}>
                      <DataGrid
                        autoHeight
                        hideFooter
                        disableRowSelectionOnClick
                        rows={Object.entries(editValues.weeklySchedule).map(
                          ([day, schedule], idx) => ({
                            id: idx,
                            dayKey: day,
                            dayTranslated: t(`space.days.${day.toLowerCase()}`, day),
                            ...schedule,
                          })
                        )}
                        columns={scheduleColumns}
                      />
                    </Grid>
                  )}
                </Grid>
              )}
            </Grid>

            <Stack direction="column" spacing={4} mt={4} mb={4}>
              {/* Gears Section */}
              <Stack spacing={2}>
                <Typography variant="subtitle1">{t('space.gears', 'Gears')}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', flexDirection: { xs: 'column', md: 'row' } }}>
                  <FormControl sx={{ minWidth: { xs: '100%', md: 200 } }} size="small">
                    <Select
                      value={newGear.gearType}
                      onChange={(e) => setNewGear({ ...newGear, gearType: e.target.value })}
                    >
                      {Object.keys(gearIcons).map((type) => (
                        <MenuItem key={type} value={type}>
                          <Box display="flex" alignItems="center" gap={1}>
                            {gearIcons[type]}
                            <span>{getGearLabel(type)}</span>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label={t('space.details', 'Details')}
                    size="small"
                    value={newGear.details}
                    onChange={(e) => setNewGear({ ...newGear, details: e.target.value })}
                    sx={{ width: { xs: '100%', md: 200 } }}
                  />
                  <AmountInput
                    amount={newGear.pricePerH.toString()}
                    label={t('space.pricePerHour', 'Price per hour')}
                    onAmountChange={(newAmount) => setNewGear({ ...newGear, pricePerH: parseFloat(newAmount) || 0 })}
                    onErrorChange={() => {}}
                    currencySymbol={currencySymbols[String(editValues.currencyCode) as keyof typeof currencySymbols] || '$'}
                    responsiveWidth={{ xs: '100%', md: 120 }}
                  />
                  <Tooltip title={t('space.addGear', 'Add Gear')}>
                    <IconButton color="primary" onClick={handleAddGear} sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {/* Gears Chip list — wraps under the inputs but aligned to the left */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flex: 1, alignItems: 'center', width: '100%' }}>
                    {gears.map((gear, index) => (
                      <Chip
                        key={index}
                        label={`${getGearLabel(gear.gearType)}${gear.details ? ': ' + gear.details : ''}${gear.pricePerH ? ` - ${gear.pricePerH.toFixed(2)} ${editValues?.currencyCode}` : ''}`}
                        variant="outlined"
                        size="small"
                        onDelete={() => handleDeleteGear(index)}
                      />
                    ))}
                  </Box>
                </Box>
              </Stack>


              {/* Amenities Section */}
              <Stack spacing={2}>
                <Typography variant="subtitle1">{t('space.amenities', 'Amenities')}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', flexDirection: { xs: 'column', md: 'row' } }}>
                  <FormControl sx={{ minWidth: { xs: '100%', md: 200 } }} size="small">
                    <Select 
                      value={newAmenity.amenityType}
                      onChange={(e) => setNewAmenity({ ...newAmenity, amenityType: e.target.value })}
                    >
                      {Object.keys(amenityIcons).map((type: keyof typeof amenityIcons) => (
                        <MenuItem key={type} value={type}>
                          <Box display="flex" alignItems="center" gap={1}>
                            {amenityIcons[type]}
                            <span>{getAmenityLabel(type)}</span>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label={t('space.details', 'Details')}
                    size="small"
                    value={newAmenity.details}
                    onChange={(e) => setNewAmenity({ ...newAmenity, details: e.target.value })}
                    sx={{ width: { xs: '100%', md: 200 } }}
                  />

                  <AmountInput
                    amount={newAmenity.pricePerH.toString()}
                    label={t('space.pricePerHour', 'Price per hour')}
                    onAmountChange={(newAmount) => setNewAmenity({ ...newAmenity, pricePerH: parseFloat(newAmount) || 0 })}
                    onErrorChange={() => {}}
                    currencySymbol={currencySymbols[String(editValues.currencyCode) as keyof typeof currencySymbols] || '$'}
                    responsiveWidth={{ xs: '100%', md: 120 }}
                  />

                  <Tooltip title={t('space.addAmenity', 'Add Amenity')} placement="left">
                    <IconButton color="primary" onClick={handleAddAmenity} sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flex: 1, alignItems: 'center', width: '100%' }}>
                    {amenities.map((amenity, index) => (
                      <Chip
                        key={index}
                        label={`${getAmenityLabel(amenity.amenityType)}${amenity.details ? ': ' + amenity.details : ''}${amenity.pricePerH ? ` - ${amenity.pricePerH.toFixed(2)} ${editValues.currencyCode}` : ''}`}
                        variant="outlined"
                        size="small"
                        onDelete={() => handleDeleteAmenity(index)}
                      />
                    ))}
                  </Box>
                </Box>
              </Stack>
            </Stack>

          <Box display="flex" gap={2} mt={4} justifyContent="flex-end">
            <Button 
              variant="contained" 
              onClick={handleSave} 
              disabled={
                !hasChanges ||
                !editValues.name.trim() ||
                !editValues.description.trim() ||
                !editValues.googleMapsUrl.trim() ||
                !editValues.city.trim()
              }
            >
              {t('common.save', 'Save')}
            </Button>
            <Button variant="outlined" onClick={handleCancel}>
              {t('common.cancel', 'Cancel')}
            </Button>
          </Box>
        </Card>
      </Box>
    </>
  );
};

export default CreateSpacePage;
